import { useToast } from '@kcd/design-system';

import NonLoginECouponProductPurchaseButton from '~/components/NonLogin/NonLoginECouponProductPurchaseButton';
import { E_COUPON_CHECKOUT } from '~/constants/checkout/variables';
import { VENDORS_ROUTER } from '~/constants/router';
import { useCurrentUser } from '~/domains/auth/queries';
import { useUpdateECoponQuick } from '~/domains/cart/queries';
import { useTrackProductEventProps } from '~/hooks/event';
import { useLinkComponent } from '~/hooks/mobileApp';
import { usePushWithQuery } from '~/hooks/url';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { UpdateECouponQuickReqParams } from '~/pages/vendors/[vendorId]/products/[offerId]/components/ECoupon/ECouponOrderButton';
import type { OAISchema } from '~/types/openapi';

import ProductImage from '../../ProductImage';
import ECouponProductPurchaseButton from '../ECouponProductPurchaseButton';
import ProductPrice from '../ProductPrice';

import PointOnly12Icon from '~/assets/icons/point_only_12x12.svg';

import styles from './index.module.css';

type Props = {
  inverse?: boolean;
  onClick?: (item: OAISchema['MarketSelectProductResponseV2']) => void;
  eventOption?: Record<string, unknown>;
  plusMembershipFullBadge?: boolean;
  reverseCouponDiscountedPrice?: boolean;
  useOrderCount?: boolean;
  is3x2?: boolean;
  isAd?: boolean;
} & OAISchema['MarketSelectProductResponseV2'];

const ECouponProductCard = ({
  eventOption,
  reverseCouponDiscountedPrice = false,
  useOrderCount = false,
  is3x2 = false,
  isAd = false,
  ...data
}: Props) => {
  const LinkComponent = useLinkComponent();
  const {
    offerId,
    vendorId,
    mainImages,
    productName,
    isOutOfStock,
    status,
    inverse,
    orderCount,
    eCouponInfo,
    eCouponOrderDisabled,
    eCouponOrderDisabledLabel,
  } = data;
  const toast = useToast();
  const nextPush = usePushWithQuery();
  const [props] = useTrackProductEventProps(data, eventOption);
  const { isLoggedIn } = useCurrentUser();

  const updateQuickECoupon = useUpdateECoponQuick();

  const onClickPurchase = async () => {
    const requestBody: UpdateECouponQuickReqParams = {
      offerId: offerId!,
    };

    try {
      await updateQuickECoupon({
        requestBody,
      });
      void nextPush(E_COUPON_CHECKOUT, false);
    } catch (error) {
      if (error instanceof OAIApiError) {
        const errMessage = getOAIErrorMessage(error);
        toast(errMessage, {
          offset: 86,
        });
      }
      throw error;
    }
  };

  return (
    <LinkComponent href={VENDORS_ROUTER.productDetail(vendorId, offerId!)}>
      <div
        className="relative"
        role="presentation"
        {...props}
        onClick={() => {
          props.onClick?.();
        }}
      >
        {mainImages![0] && (
          <ProductImage
            width={100}
            height={100}
            sizes="45vw"
            layout="responsive"
            src={mainImages![0]}
            alt={productName}
            isOutOfStock={isOutOfStock}
            status={status}
            inverse={inverse}
            rightBottom={
              isLoggedIn ? (
                <ECouponProductPurchaseButton
                  product={data}
                  onClickHandler={onClickPurchase}
                  ctaText={eCouponOrderDisabledLabel}
                  disabled={eCouponOrderDisabled}
                />
              ) : (
                <NonLoginECouponProductPurchaseButton />
              )
            }
          />
        )}
        {useOrderCount && orderCount && (
          <div className="mb-4 text-primary-regular typo-footnote300-semibold">
            {orderCount}회 이상 구매한 상품
          </div>
        )}
        {eCouponInfo?.eCouponProductType === 'FREE_POINT_ONLY' && (
          <span className="mb-4 flex items-center gap-4 text-blue-600 typo-footnote300-semibold">
            <PointOnly12Icon />
            포인트 전용 상품
          </span>
        )}
        {!is3x2 && <div className={styles.brand}>{eCouponInfo?.brandName}</div>}
        <div className={styles.title}>{productName}</div>
        <ProductPrice {...data} reverse={reverseCouponDiscountedPrice} />
        {isAd && (
          <p className="absolute bottom-0 right-0 text-gray-600 typo-footnote100-regular">
            AD
          </p>
        )}
      </div>
    </LinkComponent>
  );
};

export default ECouponProductCard;
